import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Box, Card, CardContent, Grid, Typography, Button, Tabs, Tab, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, InputLabel, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import { styled } from '@mui/system';
import IconButton from '@mui/material/IconButton';
import ImpersonateIcon from '@mui/icons-material/PeopleAlt';
import CreateUserModal from "../CreateUser/CreateUserModal";
import { createUser } from "../../features/createUser/createUserAction";
import { useDispatch, useSelector } from "react-redux";
import { getLicenseDetailAction } from "../../features/licenseDetail/licenseDetailAction";
import moment from "moment";
import CreateLicenseModal from "../CreateLicense/CreateLicenseModal";
import { editLicenseAction } from "../../features/editLicense/editLicenseAction";
import { getUserDetailAction } from "../../features/userDetail/userDetailAction";
import UserDetail from "../UserDetail/UserDetail";
import { makeStyles } from '@mui/styles';
import { getImpersonateDetailAction } from "../../features/impersonateDetail/impersonateDetailAction";
import { toast } from "react-toastify";
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import ReportDetail from "../ReportDetail/ReportDetail";

const CommonTypography = styled(Typography)({
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  width: '49%'
});

const useStyles = makeStyles({
  tabAlign: {
    cursor: 'pointer !important',
    fontWeight: '600 !important',
    textTransform: 'capitalize !important',
    fontSize: '18px !important'
  },
  tenantDetailBoxLeft: {
    justifyContent: 'space-between',
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    rowGap: '20px'
  },
  confirmButton: {
    color: 'inherit !important',
    textTransform: 'capitalize !important',
    fontWeight: '600 !important',
    borderRadius: '4px !important',
    cursor: 'pointer !important'
  },
  createUserBtn: {
    textTransform: 'capitalize !important',
    fontSize: '16px !important',
    fontWeight: '600 !important'
  },
  impersonateBox: {
    fontWeight: '600 !important',
    borderBottom: '1px solid lightgrey'
  },
  boxSpacing: {
    paddingTop: '20px !important'
  },
  impersonateContent: {
    display: 'flex',
    gap: '4px'
  },
  impersonateTenant: {
    color: '#1976d2 !important',
    display: 'contents !important'
  },
  cancelButton: {
    color: 'inherit !important',
    textTransform: 'capitalize !important',
    fontWeight: '600 !important',
    border: '1px solid',
    borderRadius: '4px !important',
    padding: '6px 16px !important',
    cursor: 'pointer'
  },
});

const TenantDetail = () => {
  const location = useLocation();
  const classes = useStyles(); 
  const { tenant } = location.state || {};
  const [tabValue, setTabValue] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [userName, setUserName] = useState("");
  const [mobileNumber, setMobileNumber] = useState();
  const [email, setEmail] = useState("");
  const [userLocation, setLocation] = useState("");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState("");
  const [licenseData, setLicenseData] = useState("");
  const [licenseUrls, setLicenseUrls] = useState("");
  const [userData, setUserData] = useState("");
  const [editLicenseModal, setEditLicenseModal] = useState(false);
  const [userRowData, setUserRowData]= useState([]);
  const [openImpersonatePopup, setOpenImpersonatePopup] = useState(false);
  const [selectedPortal, setSelectedPortal] = useState("User");
  const [isAdmin, setIsAdmin] = useState(false);
  const hasFetched = useRef(false);
  const userSearchDebounceRef = useRef();
  const [impersonateUserId, setImpersonateUserId] = useState(null);
  const [editLicenseData, setEditLicenseData] = useState({
    name: "",
    totalLicenses: "",
    status: "",
    expirationDate: "",
  });
  const [userSearchText, setUserSearchText] = useState("");
  const [licenseError, setLicenseError] = useState("");
  const dispatch = useDispatch();
  const userDetailData = useSelector((state) => state.userDetail.data);
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Memoize getLicenseDetail to avoid re-creation on every render
  const getLicenseDetail = useCallback(async (id) => {
    dispatch(getLicenseDetailAction({ licenseId: id })).then((res) => {
      if (res?.payload) {
        const licenseData = {
        ...res?.payload?.data,
        status: res?.payload?.data?.status === "Approved" ? "Active" : res?.payload?.data?.status
      };
        setLicenseData(licenseData);
        setLicenseUrls(res?.payload?.urls);
      }
    });
  }, [dispatch]);

  // Update getUserDetail to accept search
  const getUserDetail = useCallback(async (id, searchValue = "") => {
    dispatch(getUserDetailAction({ tenantId: id, search: !!searchValue, searchText: searchValue })).then((res) => {
      if (res?.payload) {
        setUserData(res?.payload?.data);
      }
    });
  }, [dispatch]);

  useEffect(() => {
    if (!hasFetched.current && tenant?.id) {
      getLicenseDetail(tenant.id);
      getUserDetail(tenant.id);
      hasFetched.current = true;
    }
  }, [tenant?.id]);

  const handleImpersonate = (userId, isAdminValue) => {
    setImpersonateUserId(userId);
    setOpenImpersonatePopup(true);
    setIsAdmin(isAdminValue);
  }

  const handleImpersonateConfirm = () => {
    let impersonateData = {
      userId: impersonateUserId,
      tenantId: tenant?.id,
      portalName: selectedPortal,
    }
    dispatch(getImpersonateDetailAction(impersonateData)).then((res) => {
      if (res?.payload && res?.payload?.success === "success") {
        const { token, user, impersonatorUrl, impersonating } = res.payload;
        if (impersonatorUrl) {
          const data = {
            token: token,
            currentUser: user,
            impersonating
          };
          function encodeData(obj) {
            const jsonString = JSON.stringify(obj);
            return btoa(jsonString);
          }
          const encodedData = encodeData(data);
          const updatedUrl = `${impersonatorUrl}login?token=${(encodedData)}`;
          window.location.href = updatedUrl;
        } else {
          console.error("Impersonator URL not available");
        }
      } else {
        console.error("Impersonation failed:", res.payload || res.error);
      }
    });
    setOpenImpersonatePopup(false);
  }

  useEffect(()=>{
    setUserRowData(userDetailData?.userList?.map((item)=> ({
      id: item?._id,
      name: item?.name,
      email: item?.email,
      mobileNumber: item?.mobileNumber,
      department: item?.department,
      location: item?.location,
      role: item?.role,
      isAdmin: item?.isAdmin,
    })))
  },[userDetailData])

  const userDataColumn = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
    },
    {
      field: 'mobileNumber',
      headerName: 'Mobile No.',
      flex: 0.6,
    },
    {
      field: 'department',
      headerName: 'Department',
      flex: 0.6,
      sortable: false,
    },
    {
      field: 'location',
      headerName: 'Location',
      flex: 0.6,
      sortable: false,
    },
    {
      field: 'role',
      headerName: 'Role',
      flex: 0.6,
      sortable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.6,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Impersonate" arrow>
          <IconButton
            sx={{color: '#1976d2'}}
            onClick={() => handleImpersonate(params.row.id, params.row.isAdmin)}
          >
            <ImpersonateIcon />
          </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (!tenant) {
    return <Typography variant="h6">No tenant details available.</Typography>;
  }

  const handleCreateUser = () => {
    setOpenModal(true);
  };
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleUserCreate = () => {     
      if (userName.trim() && mobileNumber?.length <= 10 && email && startDate) {
        let userExpireInDay = '';
        
        if (startDate && endDate) {
          const startDateTime = new Date(startDate).getTime();
          const endDateTime = new Date(endDate).getTime();
          const diff = Math.ceil((endDateTime - startDateTime) / (1000 * 60 * 60 * 24)) + 1;
          userExpireInDay = diff > 0 ? diff.toString() : '0';
        }

        const newUserData = {
          domain: tenant.id,
          mobileNumber,
          isVerified: true,
          startDate: startDate,
          endDate: endDate,
          userExpireInDay,
          name: userName,
          email,
          location: userLocation,
          department: 'CS',
          role: 'Admin',
          courses: [],
          notificationCourses: [],
          status: true,
        };
        dispatch(createUser(newUserData)).then((res) => {
          if(res.payload.data){
            toast.success(res.payload.message);
            dispatch(getUserDetailAction({tenantId: tenant?.id}))
          } else {
            toast.error("Unable to add user. Please try again later.");
          }
        }).catch(() => {
          toast.error("Unable to add user. Please try again later.");
        });
        setOpenModal(false);
        setUserName("");
        setMobileNumber("");
        setEmail("");
        setLocation("");
        setStartDate(new Date().toISOString().split("T")[0]);
        setEndDate("");
      }
    };

    const handleEditLicenses = (fieldName) => {
      const license = {
        name: licenseData?.name || "",
        totalLicenses: licenseData?.totalLicenses || "",
        status: licenseData?.status || "",
        expirationDate: licenseData?.expirationDate || "",
      };
      setEditLicenseData(license);
      setEditLicenseModal(true);
    };

    const handleEditLicenceCloseModal = () => {
        setEditLicenseModal(false);
    };

    const handleSaveLicenseChanges = () => {
        if (!editLicenseData.expirationDate) {
            toast.error("Please select an expiration date.");
            return;
        }

        // Validate totalLicenses
        const totalLicenses = Number(editLicenseData.totalLicenses);
        const usedLicenses = userDetailData?.userCount || 0;

        if (!editLicenseData.totalLicenses || isNaN(totalLicenses) || totalLicenses <= 0 || !Number.isInteger(totalLicenses)) {
          const errorMsg = "Please enter a valid positive integer for Allowed License.";
          setLicenseError(errorMsg);
          toast.error(errorMsg);
          return;
        }
        
        if (totalLicenses < usedLicenses) {
          const errorMsg = `Allowed License cannot be less than currently used licenses (${usedLicenses}).`;
          setLicenseError(errorMsg);
          toast.error(errorMsg);
          return;
        }
        
        const selectedDate = new Date(editLicenseData.expirationDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (selectedDate < today) {
            toast.error("Expiration date cannot set from the past dates.");
            return;
        }
        
        const updatedData = {
            ...editLicenseData,
            status: editLicenseData.status === "Active" ? "Approved" : editLicenseData.status,
            expirationDate: editLicenseData.expirationDate,
            name: editLicenseData.name,
        };
        dispatch(editLicenseAction({ licenseId: licenseData.id, ...updatedData })).then((res) => {
            if (res?.payload) {
                toast.success("Customer details updated successfully.");
                getLicenseDetail(res?.payload?.data?.name);
            } else {
                toast.error(res?.error?.message);
            }
        }).catch(() => {
            toast.error("Unable to update customer details. Please try again later.");
        });
        setEditLicenseModal(false);
    };



  // User search handlers
  const handleUserSearchChange = (e) => {
    const value = e.target.value;
    setUserSearchText(value);
    if (userSearchDebounceRef.current) {
      clearTimeout(userSearchDebounceRef.current);
    }
    userSearchDebounceRef.current = setTimeout(() => {
      if (tenant?.id) {
        getUserDetail(tenant.id, value);
      }
    }, 400); // 400ms debounce
  };
  const handleUserSearch = () => {
    if (tenant?.id) {
      getUserDetail(tenant.id, userSearchText);
    }
  };
  const handleUserSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleUserSearch();
    }
  };
  const handleUserSearchClear = () => {
    setUserSearchText("");
    if (userSearchDebounceRef.current) {
      clearTimeout(userSearchDebounceRef.current);
    }
    if (tenant?.id) {
      getUserDetail(tenant.id, ""); // Reset user list
    }
  };

  return (
    <>
      <Box sx={{ padding: '20px', marginTop: 5 }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider", marginBottom: '20px', display: "flex", justifyContent: 'space-between' }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab className={classes.tabAlign} label="Customer" />
              <Tab className={classes.tabAlign} label="Users" />
              <Tab className={classes.tabAlign} label="Reports" />
              </Tabs>
          </Box>

        {tabValue === 0 && (
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Card sx={{ height: "100%" }}>
                <CardContent sx={{display: 'flex', flexDirection: 'column'}}>
                  <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'}}>
                    <Typography variant="h6" fontWeight={600}>
                      Customer & License Details
                    </Typography>
                    <Box sx={{ cursor: "pointer" }}>
                        <Button
                            variant="contained"
                            onClick={handleEditLicenses}
                            sx={{ textTransform: 'capitalize', fontWeight: '600' }}
                        >
                            Edit Customer
                        </Button>
                    </Box>
                  </Box>
                </CardContent>
                <Box sx={{display: 'flex'}}>
                  <CardContent className={classes.tenantDetailBoxLeft}>
                    <CommonTypography>
                      <strong>Customer Name:</strong> {tenant.tenant}
                    </CommonTypography>
                    <CommonTypography>
                      <strong>Created On:</strong>
                      <span>{tenant.createdOn}</span>
                    </CommonTypography>
                    <CommonTypography>
                      <strong>Current Status:</strong>{" "}
                      <span style={{ color: licenseData.status === "Active" ? "green" : "red" }}>
                        {licenseData.status}
                      </span>
                    </CommonTypography>
                    <CommonTypography>
                      <strong>Expiring On:</strong> 
                      <span>{moment(licenseData?.expirationDate).format("DD-MM-YYYY")}</span>
                    </CommonTypography>
                    <CommonTypography>
                      <strong>Admin Platform Url:</strong>{" "}
                      <a href={licenseUrls?.adminUrl} style={{textDecoration: 'none', wordBreak: 'break-all'}} target="_blank" rel="noopener noreferrer">
                        {licenseUrls?.adminUrl}
                      </a>
                    </CommonTypography>
                    <CommonTypography>
                      <strong>Learner Platform Url:</strong>{" "}
                      <a href={licenseUrls?.userUrl} style={{textDecoration: 'none', wordBreak: 'break-all'}} target="_blank" rel="noopener noreferrer">
                        {licenseUrls?.userUrl}
                      </a>
                    </CommonTypography>
                    <CommonTypography>
                      <strong>Allowed License:</strong>
                      <Box display="flex" alignItems="center" gap={1}>
                        <span>{licenseData?.totalLicenses}</span>
                      </Box>
                    </CommonTypography>
                    <CommonTypography>
                      <strong>Used License:</strong>
                      <Box display="flex" alignItems="center" gap={1}>
                        <span>{userDetailData?.userCount}</span>
                      </Box>
                    </CommonTypography>
                  </CardContent>
                </Box>
              </Card>
            </Grid>
          </Grid>
        )}

        {tabValue === 1 && (
          <Box>
            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <Box sx={{ width: '300px' }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search users"
                value={userSearchText}
                onChange={handleUserSearchChange}
                onKeyDown={handleUserSearchKeyDown}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {userSearchText ? (
                        <IconButton onClick={handleUserSearchClear}>
                          <CloseIcon />
                        </IconButton>
                      ) : (
                        <IconButton onClick={handleUserSearch}>
                          <SearchIcon />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Button  onClick={handleCreateUser} variant="contained" color="primary" className={classes.createUserBtn}>
              Add New User
            </Button>
            </Box>
            <Typography variant="h6" sx={{ textAlign: "center", marginTop: '20px' }}>
              <UserDetail columns={userDataColumn} rows={userRowData}/>
            </Typography>
          </Box>
        )}

        {tabValue === 2 && (
          <Typography variant="h6" sx={{ textAlign: "center", marginTop: 4 }}>
            <ReportDetail/>
          </Typography>
        )}

        <CreateUserModal openModal={openModal} handleCloseModal={handleCloseModal} handleUserCreate={handleUserCreate}
        userName={userName} setUserName={setUserName} mobileNumber={mobileNumber}
        setMobileNumber={setMobileNumber} email={email} setEmail={setEmail} userLocation={userLocation} setLocation={setLocation}
        startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate} licenseData={licenseData}/>

        <CreateLicenseModal
            name={tenant?.tenant}
            domainName={editLicenseData.name}
            totalLicenses={editLicenseData.totalLicenses}
            setTotalLicenses={(val) =>
              setEditLicenseData((prev) => ({ ...prev, totalLicenses: val }))
            }
            status={editLicenseData.status}
            setStatus={(val) =>
              setEditLicenseData((prev) => ({ ...prev, status: val }))
            }
            expirationDate={editLicenseData.expirationDate}
            setExpirationDate={(val) =>
              setEditLicenseData((prev) => ({ ...prev, expirationDate: val }))
            }
            usedLicenses={userDetailData?.userCount}
            openModal={editLicenseModal}
            handleCloseModal={handleEditLicenceCloseModal}
            handleSave={handleSaveLicenseChanges}
            licenseError={licenseError}
        />
        <Dialog open={openImpersonatePopup} onClose={() => setOpenImpersonatePopup(false)}
          sx={{
            '& .MuiDialog-paper': {
              width: '650px',
              maxWidth: '100%',
            },
          }}
        >
          <DialogTitle className={classes.impersonateBox}>Impersonate</DialogTitle>
          <DialogContent className={classes.boxSpacing}>
            <Typography className={classes.impersonateContent}>Please select the portal in which you want to Impersonate in <InputLabel className={classes.impersonateTenant}>{tenant?.tenant}</InputLabel></Typography>
            <RadioGroup sx={{display: 'flex', flexDirection: 'row'}} value={selectedPortal} onChange={(e) => setSelectedPortal(e.target.value)}>
              <FormControlLabel value="Admin" control={<Radio disabled={!isAdmin} />} label="Admin Portal" />
              <FormControlLabel value="User" control={<Radio />} label="User Portal" />
            </RadioGroup>
          </DialogContent>
          <DialogActions sx={{borderTop: '1px solid lightgrey' }}>
            <Button onClick={() => setOpenImpersonatePopup(false)}>
              <InputLabel className={classes.cancelButton}>Cancel</InputLabel>
            </Button>
            <Button onClick={handleImpersonateConfirm} variant="contained" color="primary" disabled={!selectedPortal} sx={{marginRight: '0px'}}>
              <InputLabel className={classes.confirmButton}>Confirm</InputLabel>
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default TenantDetail;
